import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronRight } from "@material-symbols-svg/react";
import moment from "moment";

import AddButtonOverlay from "src/components/common/AddButtonOverlay";
import InsightHistory from "./InsightHistory";
import MedicationInsightView from "./MedicationInsightView";
import PeriodNavigator, { PeriodSegment } from "./PeriodNavigator";
import BarChart, { BarChartDatum } from "../charts/BarChart";
import InsightsProgressCard from "./InsightsProgressCard";

import { useAppSelector } from "src/redux/hooks";
import insightsApis from "src/api/insights";
import {
  LOGGERS_TYPES,
  DEFAULT_GOAL_VALUES,
  DEFAULT_UNITS,
} from "src/constants/loggers";

type FilterType = "day" | "week" | "month";

const TAB_TO_FILTER: Record<string, FilterType> = {
  daily: "day",
  weekly: "week",
  monthly: "month",
};

function toApiDate(m: moment.Moment): string {
  return m.format("DD/MM/YYYY");
}

function toApiMonth(m: moment.Moment): string {
  return m.format("MMM").toUpperCase();
}

interface GraphItem {
  timestamp?: string;
  value?: number;
  [key: string]: unknown;
}

export interface InsightComponentHealthOption {
  icon: React.ReactNode;
  label: string;
  onClick?: () => void;
}

interface InsightComponentHealthProps {
  insightType: string;
  parameter: string;
  barColor?: string;
  icon?: React.ReactNode;
  supportProgramKey: string;
  options?: InsightComponentHealthOption[];
}

const InsightComponentHealth: React.FC<InsightComponentHealthProps> = ({
  parameter,
  barColor = "#69b48c",
  icon,
  supportProgramKey,
  options = [],
}) => {
  const { patient } = useAppSelector((state) => state.auth);

  const [viewType, setViewType] = useState<"chart" | "history">("chart");
  const [activeTab, setActiveTab] = useState<PeriodSegment>("daily");
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [periodOffset, setPeriodOffset] = useState(0);
  const [graphData, setGraphData] = useState<GraphItem[]>([]);
  const [unitFromApi, setUnitFromApi] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const loggerType =
    LOGGERS_TYPES[parameter as keyof typeof LOGGERS_TYPES] ?? "";

  const buildQueryParams = useCallback(() => {
    const filterType = TAB_TO_FILTER[activeTab] ?? "day";
    const base: Record<string, string | number> = {
      user_id: patient?.data?.patient_id ?? "",
      logger: loggerType,
      filter_type: filterType,
    };
    if (filterType === "day") {
      const date = moment().subtract(periodOffset, "day");
      base.date = toApiDate(date);
    } else if (filterType === "week") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      const end = start.clone().endOf("week");
      base.start_date = toApiDate(start);
      base.end_date = toApiDate(end);
    } else {
      const monthM = moment().subtract(periodOffset, "month").startOf("month");
      base.month = toApiMonth(monthM);
      base.year = monthM.year();
    }
    return base;
  }, [activeTab, periodOffset, patient?.data?.patient_id, loggerType]);

  const fetchLogs = useCallback(async () => {
    const userId = patient?.data?.patient_id;
    if (!userId || !loggerType) {
      setGraphData([]);
      setUnitFromApi(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const queryParams = buildQueryParams();
      const res: any = await insightsApis.getLogData(queryParams);
      const code = res?.code != null ? Number(res.code) : null;
      if (code === -1 || code === 0) {
        setGraphData([]);
        setUnitFromApi(null);
        return;
      }
      const list = res?.data?.graph_data ?? [];
      setGraphData(Array.isArray(list) ? list : []);
      setUnitFromApi(res?.data?.unit ?? null);
    } catch {
      setGraphData([]);
      setUnitFromApi(null);
    } finally {
      setLoading(false);
    }
  }, [patient?.data?.patient_id, loggerType, buildQueryParams]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const toTimestamp = (ts: string): number | null => {
    if (!ts) return null;
    const cleaned = String(ts).replace("TZ", "T");
    const m = moment(cleaned, [moment.ISO_8601, "YYYY-MM-DD"], true);
    return m.isValid() ? m.valueOf() : null;
  };

  const toYMD = (ts: string): string | null => {
    if (!ts) return null;
    const cleaned = String(ts).replace("TZ", "T");
    const m = moment(cleaned, [moment.ISO_8601, "YYYY-MM-DD"], true);
    return m.isValid() ? m.format("YYYY-MM-DD") : null;
  };

  const { rangeStart, rangeEnd } = useMemo(() => {
    if (activeTab === "daily") {
      const d = moment().subtract(periodOffset, "day");
      return {
        rangeStart: d.clone().startOf("day").valueOf(),
        rangeEnd: d.clone().endOf("day").valueOf(),
      };
    }
    if (activeTab === "weekly") {
      const start = moment().subtract(periodOffset, "week").startOf("week");
      return {
        rangeStart: start.valueOf(),
        rangeEnd: start.clone().endOf("week").valueOf(),
      };
    }
    const start = moment().subtract(periodOffset, "month").startOf("month");
    return {
      rangeStart: start.valueOf(),
      rangeEnd: start.clone().endOf("month").valueOf(),
    };
  }, [activeTab, periodOffset]);

  const chartData = useMemo((): BarChartDatum[] => {
    const valueByDay = new Map<string, number>();
    graphData.forEach((item) => {
      const ymd = toYMD(item?.timestamp as string);
      if (!ymd) return;
      const t = toTimestamp(item?.timestamp as string);
      if (t == null || t < rangeStart || t > rangeEnd) return;
      const val = item?.value;
      const num = typeof val === "number" && Number.isFinite(val) ? val : 0;
      valueByDay.set(ymd, (valueByDay.get(ymd) ?? 0) + num);
    });

    if (activeTab === "daily") {
      const d = moment(rangeStart);
      const ymd = d.format("YYYY-MM-DD");
      const value = valueByDay.get(ymd) ?? 0;
      const label =
        periodOffset === 0
          ? "Today"
          : periodOffset === 1
          ? "Yesterday"
          : d.format("D MMM");
      return [{ label, value }];
    }

    if (activeTab === "weekly") {
      const start = moment(rangeStart);
      return Array.from({ length: 7 }, (_, i) => {
        const day = start.clone().add(i, "day");
        const ymd = day.format("YYYY-MM-DD");
        const value = valueByDay.get(ymd) ?? 0;
        return { label: day.format("D MMM"), value };
      });
    }

    if (activeTab === "monthly") {
      const start = moment(rangeStart);
      const daysInMonth = moment(rangeEnd).date();
      return Array.from({ length: daysInMonth }, (_, i) => {
        const day = start.clone().date(i + 1);
        const ymd = day.format("YYYY-MM-DD");
        const value = valueByDay.get(ymd) ?? 0;
        return { label: day.format("D"), value };
      });
    }

    return [];
  }, [graphData, rangeStart, rangeEnd, activeTab, periodOffset]);

  const unit = unitFromApi ?? DEFAULT_UNITS[loggerType] ?? "";

  const isMedication = parameter === "medication-logs";

  if (isMedication) {
    return (
      <MedicationInsightView
        supportProgramKey={supportProgramKey}
        options={options}
        icon={icon}
      />
    );
  }

  if (viewType === "history") {
    return (
      <InsightHistory
        onBackClick={() => setViewType("chart")}
        supportProgramKey={supportProgramKey}
        logger={loggerType}
        icon={icon}
      />
    );
  }

  return (
    <div className="relative pb-12">
      <div className="px-4 space-y-4">
        <InsightsProgressCard loggerType={loggerType} />

        <div className="bg-white rounded-xl p-4 space-y-4">
          <PeriodNavigator
            activeTab={activeTab}
            periodOffset={periodOffset}
            onTabChange={(tab) => {
              setActiveTab(tab);
              setPeriodOffset(0);
            }}
            onPrev={() => setPeriodOffset((p) => p + 1)}
            onNext={() => setPeriodOffset((p) => Math.max(0, p - 1))}
          />

          {loading ? (
            <div className="h-40 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-[var(--app-primary)] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : chartData.length === 0 ||
            chartData.every((d) => d.value === 0) ? (
            <div className="h-40 flex items-center justify-center text-sm text-gray-500">
              No data for this period
            </div>
          ) : (
            <BarChart
              data={chartData}
              unit={unit}
              activeIndex={activeIndex}
              onBarClick={(index) => setActiveIndex(index)}
              barColor={barColor}
            />
          )}
        </div>
      </div>

      <div className="p-4 space-y-3">
        <button
          type="button"
          onClick={() => setViewType("history")}
          className="w-full bg-white rounded-xl px-4 py-3 flex items-center justify-between shadow-sm border border-gray-100"
        >
          <span>Show All Data</span>
          <ChevronRight className="w-6 h-6" />
        </button>
      </div>

      <AddButtonOverlay options={options} />
    </div>
  );
};

export default InsightComponentHealth;
